import DOMPurify from 'dompurify';

/**
 * Decodes HTML entities and sanitizes the content using DOMPurify.
 * This resolves issues where backend data contains HTML entities like &gt; instead of >.
 * @param text - The text potentially containing HTML entities
 * @returns The decoded and sanitized text
 */
export const decodeAndSanitize = (text: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    const decoded = textArea.value;
    return DOMPurify.sanitize(decoded);
};

// A `<br>` or the end of a block element is where a line ended in the text that was written.
const LINE_BREAK = /<br\s*\/?>|<\/(?:p|div|li|h[1-6]|blockquote)>/gi;

/**
 * Strips every tag from an HTML string, leaving its plain text with entities decoded (e.g.
 * `&amp;` becomes `&`) — for rich text (`RichText`/`slateToHtml` output) measured or shown somewhere
 * that only renders plain text. The result is a plain string: render it as text, never as HTML.
 * @param html - The HTML markup to reduce to text
 * @param lineBreak - What each line break of the markup becomes; dropped by default, which is how
 * the editor counts the text
 * @returns The tag-free, entity-decoded text
 */
export const htmlToPlainText = (html: string, lineBreak = ''): string => {
    // `ALLOWED_TAGS: []` still HTML-escapes the text it keeps; the `<textarea>` round-trip decodes it.
    const strippedHtml = DOMPurify.sanitize(html.replace(LINE_BREAK, lineBreak), {
        ALLOWED_TAGS: [],
    });
    const textArea = document.createElement('textarea');
    textArea.innerHTML = strippedHtml;
    return textArea.value;
};
